import { useState, useEffect, useRef } from 'react'
import debounce from 'lodash.debounce'

import { preconUserId } from 'environment'

import ToastService from 'services/toast.service'
import SaveService from 'services/save.service'
import DeckService from 'services/deck.service'

import { FormatType } from 'types/deck'
import { DeckListResponse } from 'services/apiTypes/deck.types'
import { DeckCardInfoType } from 'types/search'

import DeckLink from 'components/misc/DeckLink'
import LineLoader from 'components/elements/LineLoader'
import PhatInput from 'components/formElements/PhatInput'
import PhatButton from 'components/formElements/PhatButton'

import styles from './preconSelector.module.scss'

type Props = {
  format: FormatType
  onClose: () => void
}

const PreconSelector = ({ format, onClose }: Props) => {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [response, setResponse] = useState<DeckListResponse>()

  const [currentPage, setCurrentPage] = useState(1)
  const [fetchingDeck, setFetchingDeck] = useState(false)

  const handleFetch = (filter = '') => {
    if (preconUserId === null) return setLoading(false)

    setLoading(true)
    setCurrentPage(1)

    DeckService.listV3({ deckFormat: format, ownerId: preconUserId, name: filter })
      .then(setResponse)
      .catch(setError)
      .finally(() => {
        setLoading(false)
      })
  }

  const loadNext = () => {
    if (!response?.next || preconUserId === null) return

    setLoading(true)

    // response.next doesn't work in the browser due to the API returning http rather than https. We should fix that
    // and use next instead here
    DeckService.listV3({}, response.next)
      .then(res => setResponse({ ...res, results: [...response.results, ...res.results] }))
      .catch(setError)
      .finally(() => setLoading(false))

    setCurrentPage(currentPage + 1)
  }

  const handleAddDeckToState = async (deck: DeckCardInfoType) => {
    setFetchingDeck(true)

    return DeckService.details(deck.id)
      .then(precon => {
        const cardsWithDeckRelationIdRemoved = precon.cards.map(c => ({ ...c, id: '' }))

        return SaveService.overwriteDeck({ ...precon, cards: cardsWithDeckRelationIdRemoved }).then(() => onClose())
      })
      .catch(err => {
        ToastService.create(
          'Unable to load precon or overwrite your deck. If this problem persists please contact support.',
          'Precon helper',
          'error',
        )

        console.error(err)
      })
      .finally(() => setFetchingDeck(false))
  }

  const debouncedSeach = useRef(debounce(handleFetch, 500)).current

  useEffect(handleFetch, [format])

  if (error) return <div className={styles.container}>{error}</div>
  if (preconUserId === null) return <div>Woah, not sure what happened. But you should not be here</div>
  if (fetchingDeck) return <LineLoader className={styles.container} />

  return (
    <div className={styles.container}>
      <div className={styles.controls}>
        <h2>Select a Precon</h2>

        <PhatInput header="Filter precons" placeholder="Swell the Host" onChange={debouncedSeach} />
      </div>

      {loading && <LineLoader />}

      <div className={styles.preconGrid}>
        {response?.results.map((deck, index) => (
          <button key={index} onClick={() => handleAddDeckToState(deck)}>
            <DeckLink deck={deck} displayOnly />
          </button>
        ))}
      </div>

      {loading && !!response?.results.length && <LineLoader />}

      <div className={styles.loadMore}>
        {response?.next && !loading && (
          <PhatButton color="blue" onClick={loadNext}>
            Load More
          </PhatButton>
        )}
      </div>
    </div>
  )
}

export default PreconSelector
